// src/utils/formatters.js
/**
 * Shared formatting utilities for consistent data display
 */

/**
 * Format DIR score with appropriate sign and precision
 * @param {number|null|undefined} score - DIR score
 * @returns {string} Formatted DIR score (e.g., "+2.5", "-1.3", "N/A")
 */
export const formatDirScore = (score) => {
  if (score === null || score === undefined || isNaN(score)) return 'N/A';
  const formattedScore = Number(score).toFixed(1);
  return Number(formattedScore) > 0 ? `+${formattedScore}` : formattedScore;
};

/**
 * Format percentage with specified decimal places
 * @param {number|null|undefined} value - Numeric value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage (e.g., "85.5%", "N/A")
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format score with specified decimal places
 * @param {number|null|undefined} value - Numeric value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted score (e.g., "4.2", "N/A")
 */
export const formatScore = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return Number(value).toFixed(decimals);
};

/**
 * Format currency value with proper thousands separators
 * @param {number|string|null|undefined} value - Numeric value or currency string
 * @returns {string} Formatted currency (e.g., "$1,234,567", "$0")
 */
export const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';

  // Convert to number if it's a string (handle $, commas)
  const numValue = typeof value === 'string' ?
    parseFloat(value.replace(/[$,]/g, '')) :
    Number(value);

  if (isNaN(numValue)) return '$0';

  // Format number with commas and round to nearest dollar
  return `$${Math.round(numValue).toLocaleString('en-US')}`;
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date (e.g., "Dec 15, 2024")
 */
export const formatDate = (date, options = {}) => {
  if (!date) return 'N/A';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';

    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options
    };

    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Format date and time for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time (e.g., "Dec 15, 2024, 3:30 PM")
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';

    return dateObj.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Standardize employee ID format (ensure EMP prefix)
 * @param {string|number} id - Employee ID
 * @returns {string} Standardized employee ID (e.g., "EMP12345")
 */
export const standardizeEmployeeId = (id) => {
  if (!id) return id;
  const stringId = String(id);
  return stringId.startsWith('EMP') ? stringId : `EMP${stringId}`;
};

/**
 * Format category name (convert snake_case to Title Case)
 * @param {string} categoryId - Category identifier in snake_case
 * @returns {string} Formatted category name (e.g., "Software Development")
 */
export const formatCategoryName = (categoryId) => {
  if (!categoryId) return '';
  return categoryId
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Clean JSON string by replacing invalid values (NaN, Infinity)
 * @param {string} jsonString - JSON string to clean
 * @returns {string} Cleaned JSON string
 */
export const cleanJsonString = (jsonString) => {
  return jsonString
    .replace(/:\s*NaN\s*([,}])/g, ': null$1')
    .replace(/:\s*Infinity\s*([,}])/g, ': null$1')
    .replace(/:\s*-Infinity\s*([,}])/g, ': null$1');
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format large numbers with commas
 * @param {number} value - Number to format
 * @returns {string} Formatted number (e.g., "1,234,567")
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Format hours with decimal precision
 * @param {number} hours - Hours value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted hours (e.g., "42.5")
 */
export const formatHours = (hours, decimals = 1) => {
  if (hours === null || hours === undefined || isNaN(hours)) return 'N/A';
  return Number(hours).toFixed(decimals);
};

/**
 * Format project ID with P prefix
 * @param {string|number} projectId - Project ID
 * @returns {string|null} Formatted project ID (e.g., "P12345")
 */
export const formatProjectId = (projectId) => {
  if (!projectId) return null;
  const idStr = projectId.toString();
  return idStr.startsWith('P') ? idStr : `P${idStr}`;
};

/**
 * Get display version of project ID (without P prefix)
 * @param {string|number} projectId - Project ID
 * @returns {string} Display project ID (e.g., "12345")
 */
export const getDisplayProjectId = (projectId) => {
  if (!projectId) return '';
  return projectId.toString().replace(/^P-?/i, '');
};

/**
 * Format client ID with C prefix
 * @param {string|number} clientId - Client ID
 * @returns {string|null} Formatted client ID (e.g., "C12345")
 */
export const formatClientId = (clientId) => {
  if (!clientId) return null;
  const idStr = clientId.toString();
  return idStr.startsWith('C') ? idStr : `C${idStr}`;
};

/**
 * Get display version of client ID (without C prefix)
 * @param {string|number} clientId - Client ID
 * @returns {string} Display client ID (e.g., "12345")
 */
export const getDisplayClientId = (clientId) => {
  if (!clientId) return '';
  return clientId.toString().replace(/^C-?/i, '');
};
